import ReactFlow from "reactflow";
import { PtwSettingsData } from "./workFlow-context";
import { useReactFlow, ReactFlowProvider } from "reactflow";
import "reactflow/dist/style.css";
import { Box, IconButton, Typography, Fade, Grow } from "@mui/material";
import { useTheme, lighten, darken } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";

const WorkFlowPreview = () => {
  const { t } = useTranslation();
  const {
    nodes,
    edges,
    onNodesChange,
    //onConnect,
    defaultEdgeOptions,
    isLoading,
    goBackToList,
  } = PtwSettingsData();
  const { fitView } = useReactFlow();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [showFlow, setShowFlow] = useState(false);

  // Auto-fit view when nodes change with delayed show for smooth transition
  useEffect(() => {
    if (nodes.length > 0) {
      const timer = setTimeout(() => {
        fitView({ padding: 0.1, includeHiddenNodes: false });
        setShowFlow(true);
      }, 200); // Slightly longer delay for smoother transition
      return () => clearTimeout(timer);
    } else {
      setShowFlow(false);
    }
  }, [nodes, fitView]);

  if (isLoading)
    return <Typography>{t("workFlowPreview.loadingText")}</Typography>;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          width: "calc(100vw - 32px)", // Full width minus padding
          position: "relative",
          height: "calc(100vh - 32px)", // Full height minus padding
          overflow: "visible", // Prevent scrollbars
          p: 2, // Add some padding
          m: 0, // Reset margin to avoid gaps
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            flex: 1, // Fill remaining space
            width: "100%", // Ensure full width
            height: "100%", // Ensure full height
            position: "relative", // Allow absolute positioning of Button
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Fade in timeout={400}>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 2,
                height: "48px", // Fixed height for header
              }}
            >
              <IconButton
                onClick={goBackToList}
                aria-label={t("workFlowPreview.backButtonAria")}
                sx={{
                  bgcolor: theme.palette.background.paper,
                  boxShadow: theme.shadows[2],
                  "&:hover": {
                    bgcolor: theme.palette.background.paper,
                    boxShadow: theme.shadows[4],
                    transform: "scale(1.05)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t("workFlowPreview.title")}
              </Typography>
            </Box>
          </Fade>
          <Box
            sx={{
              width: "100%",
              height: "calc(100vh - 120px)", // Fixed height minus header and padding
              minHeight: "500px", // Minimum fallback height
            }}
          >
            {nodes.length > 0 ? (
              <Grow in={showFlow} timeout={800}>
                <Box sx={{ width: "100%", height: "100%" }}>
                  <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    // onConnect={onConnect}
                    defaultEdgeOptions={defaultEdgeOptions}
                    fitView
                    fitViewOptions={{ padding: 0.1 }}
                    snapToGrid={true}
                    snapGrid={[10, 10]}
                    minZoom={0.1}
                    maxZoom={1.5}
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: 8,
                      border: `1px solid ${
                        isDarkMode
                          ? lighten(theme.palette.divider, 0.8)
                          : darken(theme.palette.divider, 0.5)
                      }`,
                    }}
                  />
                </Box>
              </Grow>
            ) : (
              <Fade in timeout={500}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    border: `1px solid ${
                      isDarkMode
                        ? lighten(theme.palette.divider, 0.8)
                        : darken(theme.palette.divider, 0.5)
                    }`,
                    borderRadius: 2,
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography variant="h6" color="text.secondary">
                    {t("workFlowPreview.noNodesText") ||
                      "No workflow steps to display"}
                  </Typography>
                </Box>
              </Fade>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default function MainFunction() {
  return (
    <ReactFlowProvider>
      <WorkFlowPreview />
    </ReactFlowProvider>
  );
}
